import React, { useEffect, useState } from 'react'
import { useAtom } from 'jotai'
import moment from 'moment'
import { IkonContainerAfrapportering } from '../../../../assets/icons'
import { CaseDetail, Status } from '../../../../models/CaseDetail'
import { CardColumn, Row } from '../../../ui/Card/CardColumn'
import { Select } from '../../../ui/FormElememts/Select'
import {
    getCaseReportingStatusList,
    updateCaseReporting,
} from '../../../../api/salary'
import { CaseReportingList } from '../../../../models/CaseReportingList'
import { Button } from '../../../ui/Button/Button'
import { profileAtom } from '../../../../api/profile/profile'
import { DatePickerInput } from '../../../ui/FormElememts/DatePickerInput'
import { FormMessage } from '../../../ui/FormElememts/FormMessage'
import { L } from '../../../providers/Lokalise'
import { colors } from '../../../../theme/palette'
import { useErrorDialog } from '../../../../hooks/useErrorDialog'
import { RestrictElementWithTooltip } from '../RestrictElementWithTooltip'
import { caseDetailsUnsavedChangesAtom } from '../CaseDetails/CloseCaseRow'
import { ErrorDisplay } from '../Contact/ContactDialog'

export type MergedCasesRowProps = {
    caseDetails: CaseDetail
    onUpdated?: () => void
}

const CaseReportingRow: React.FC<MergedCasesRowProps> = ({
    caseDetails,
    onUpdated,
}) => {
    const { showError, ErrorDialogComponent } = useErrorDialog() as any
    const [authProfile] = useAtom(profileAtom)
    const [caseDetailsUnsavedChanges, setCaseDetailsUnsavedChanges] = useAtom(
        caseDetailsUnsavedChangesAtom
    )
    const [caseReportingStatusList, setCaseReportingStatusList] = useState<
        CaseReportingList | undefined
    >(undefined)
    const [
        selectedKontaktTilNyansatStatusId,
        setSelectedKontaktTilNyansatStatusId,
    ] = useState<Status | undefined>(undefined)
    const [selectedMedlemsskabStatusId, setSelectedMedlemsskabStatusId] =
        useState<Status | undefined>(undefined)
    const [
        selectedLoenindplaceringStatusId,
        setSelectedLoenindplaceringStatusId,
    ] = useState<Status | undefined>(undefined)
    const [showSubmitButton, setShowSubmitButton] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [selectedKontaktTilNyansatDate, setSelectedKontaktTilNyansatDate] =
        useState<string | undefined>(undefined)
    const [showKontaktTilNyansatDate, setShowKontaktTilNyansatDate] =
        useState<boolean>(false)
    const [kontaktTilNyansatDateError, setKontaktTilNyansatDateError] =
        useState<{
            hasError: boolean
            message: string
            show: boolean
        }>({
            hasError: false,
            message: 'Datoen er ikke gyldig',
            show: false,
        })

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            const response = await getCaseReportingStatusList()
            setCaseReportingStatusList(CaseReportingList.build(response))
            if (caseDetails?.caseProcessing) {
                if (caseDetails.caseProcessing.newlyHiredStatus) {
                    setSelectedKontaktTilNyansatStatusId({
                        ...caseDetails.caseProcessing.newlyHiredStatus,
                    })
                }
                if (caseDetails.caseProcessing.salaryPlacementStatus) {
                    setSelectedLoenindplaceringStatusId({
                        ...caseDetails.caseProcessing.salaryPlacementStatus,
                    })
                }
                if (caseDetails.caseProcessing.organizationStatus) {
                    setSelectedMedlemsskabStatusId({
                        ...caseDetails.caseProcessing.organizationStatus,
                    })
                }
                if (caseDetails.caseProcessing.contactNewlyHiredDate) {
                    setSelectedKontaktTilNyansatDate(
                        caseDetails.caseProcessing.contactNewlyHiredDate
                    )
                }
            }
        }
        fetchData()
    }, [caseDetails])

    const handleChangeValue = (
        value: string,
        key: keyof CaseReportingList
    ): void => {
        setCaseDetailsUnsavedChanges(true)
        setKontaktTilNyansatDateError((prev) => ({
            ...prev,
            hasError: false,
            message: '',
            show: false,
        }))
        switch (key) {
            case 'kontaktTilNyansatStatusList':
                setSelectedKontaktTilNyansatStatusId({
                    ...caseReportingStatusList?.kontaktTilNyansatStatusList?.find(
                        (status) => status.statusId.toString() === value
                    ),
                })
                break
            case 'loenindplaceringStatusList':
                setSelectedLoenindplaceringStatusId({
                    ...caseReportingStatusList?.loenindplaceringStatusList?.find(
                        (status) => status.statusId.toString() === value
                    ),
                })
                break
            case 'organisaeringStatusList':
                setSelectedMedlemsskabStatusId({
                    ...caseReportingStatusList?.organisaeringStatusList?.find(
                        (status) => status.statusId.toString() === value
                    ),
                })
                break
            default:
                break
        }
    }

    const handleChangeKontaktTilNyansatStatusId = (
        e: React.ChangeEvent<HTMLSelectElement>
    ): void => {
        setShowSubmitButton(true)
        handleChangeValue(e.target.value, 'kontaktTilNyansatStatusList')
    }

    const handleChangeLoenindplaceringStatusId = (
        e: React.ChangeEvent<HTMLSelectElement>
    ): void => {
        setShowSubmitButton(true)
        handleChangeValue(e.target.value, 'loenindplaceringStatusList')
    }

    const handleChangeMedlemsskabStatusId = (
        e: React.ChangeEvent<HTMLSelectElement>
    ): void => {
        setShowSubmitButton(true)
        handleChangeValue(e.target.value, 'organisaeringStatusList')
    }

    const handleSubmit = async (): Promise<void> => {
        if (kontaktTilNyansatDateError.hasError) {
            setTimeout(() => {
                setKontaktTilNyansatDateError((prev) => ({
                    ...prev,
                    show: true,
                }))
            }, 100)
            return
        }
        setIsLoading(true)
        setCaseDetailsUnsavedChanges(false)
        const updatePayload: any = {
            caseId: caseDetails.caseId,
            caseRegTime: caseDetails.caseRegistrationTime,
            kontaktTilNyansatStatusId:
                selectedKontaktTilNyansatStatusId?.statusId,
            medlemsskabStatusId: selectedMedlemsskabStatusId?.statusId,
            loenindplaceringStatusId:
                selectedLoenindplaceringStatusId?.statusId,
        }
        if (selectedKontaktTilNyansatDate) {
            updatePayload.contactNewlyHiredDate = selectedKontaktTilNyansatDate
        }
        const response = await updateCaseReporting(updatePayload)

        if (response) {
            if (response.error) {
                const keys = Object.keys(response.error.errors)
                if (keys.includes('KontaktTilNyansatDato')) {
                    setKontaktTilNyansatDateError((prev) => ({
                        ...prev,
                        hasError: true,
                        message: 'Dato for kontakt er påkrævet',
                        show: true,
                    }))
                    setIsLoading(false)
                    return
                }
                showError(<ErrorDisplay errors={response.error.errors} />)
                setIsLoading(false)
                return
            }
            onUpdated?.()
            setShowSubmitButton(false)
        }
        setIsLoading(false)
    }

    const resetForm = (): void => {
        setShowSubmitButton(false)

        if (caseDetails?.caseProcessing) {
            if (caseDetails.caseProcessing.newlyHiredStatus) {
                setSelectedKontaktTilNyansatStatusId(
                    caseDetails.caseProcessing.newlyHiredStatus
                )
            }
            if (caseDetails.caseProcessing.salaryPlacementStatus) {
                setSelectedLoenindplaceringStatusId(
                    caseDetails.caseProcessing.salaryPlacementStatus
                )
            }
            if (caseDetails.caseProcessing.organizationStatus) {
                setSelectedMedlemsskabStatusId(
                    caseDetails.caseProcessing.organizationStatus
                )
            }
        }
    }

    const renderRows = (): Row[] => {
        const rows: Row[] = []

        if (caseDetails?.caseProcessing) {
            rows.push({
                label: L('LØN_SAGS_AFRAPPORTERING_KONTAKT_TIL_NYANSAT_LABEL'),
                value: caseDetails.caseProcessing.newlyHiredStatus?.statusName,
            })
            if (caseDetails.caseProcessing.contactNewlyHiredDate) {
                rows.push({
                    label: L(
                        'LØN_SAGS_AFRAPPORTERING_KONTAKT_TIL_NYANSAT_DATO_LABEL'
                    ),
                    value: moment(
                        caseDetails.caseProcessing.contactNewlyHiredDate
                    ).format('DD.MM.YYYY'),
                })
            }
            rows.push({
                label: L('LØN_SAGS_AFRAPPORTERING_ORGANISERING_LABEL'),
                value: caseDetails.caseProcessing.organizationStatus
                    ?.statusName,
            })
            rows.push({
                label: L('LØN_SAGS_AFRAPPORTERING_LØNINDPLACERING_LABEL'),
                value: caseDetails.caseProcessing.salaryPlacementStatus
                    ?.statusName,
            })
        }
        return rows
    }

    const renderForm = (): JSX.Element => {
        return (
            <form
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '.7rem',
                    width: '100%',
                }}
            >
                {caseReportingStatusList &&
                    caseReportingStatusList.kontaktTilNyansatStatusList && (
                        <>
                            <div>
                                <RestrictElementWithTooltip
                                    restrictedAction={
                                        !caseDetails?.allowedUpdateActionList?.includes(
                                            'Sagsbehandle'
                                        )
                                    }
                                >
                                    <Select
                                        options={caseReportingStatusList?.kontaktTilNyansatStatusList?.map(
                                            (status) => ({
                                                label: status.statusName,
                                                value: status.statusId.toString(),
                                            })
                                        )}
                                        placeholder={L(
                                            'LØN_SAGS_AFRAPPORTERING_KONTAKT_TIL_NYANSAT_PLACEHOLDER'
                                        )}
                                        name="kontaktTilNyansatStatusId"
                                        defaultValue={
                                            caseDetails.caseProcessing
                                                .newlyHiredStatus?.statusId
                                        }
                                        value={selectedKontaktTilNyansatStatusId?.statusId.toString()}
                                        hasError={
                                            !!selectedKontaktTilNyansatStatusId?.statusMissing &&
                                            caseDetails?.caseStatus
                                                ?.statusType !==
                                                'AfventerAccept'
                                        }
                                        onChange={
                                            handleChangeKontaktTilNyansatStatusId
                                        }
                                        disabled={
                                            !caseDetails?.allowedUpdateActionList?.includes(
                                                'Sagsbehandle'
                                            ) || authProfile.isImitator
                                        }
                                    />
                                </RestrictElementWithTooltip>
                            </div>
                            {selectedKontaktTilNyansatStatusId &&
                                !!selectedKontaktTilNyansatStatusId.dateRequired && (
                                    <div>
                                        <DatePickerInput
                                            label={`${L(
                                                'LØN_SAGS_AFRAPPORTERING_KONTAKT_TIL_NYANSAT_DATO_LABEL'
                                            )} *`}
                                            selected={
                                                selectedKontaktTilNyansatDate !==
                                                    null &&
                                                !!selectedKontaktTilNyansatDate
                                                    ? new Date(
                                                          selectedKontaktTilNyansatDate
                                                      )
                                                    : null
                                            }
                                            maxDate={moment().toDate()}
                                            minDate={moment()
                                                .subtract(1, 'year')
                                                .toDate()}
                                            onChangeDateInputInvalid={(
                                                isValid,
                                                message
                                            ) => {
                                                setKontaktTilNyansatDateError({
                                                    hasError: !isValid,
                                                    message:
                                                        message ??
                                                        'Datoen er ikke gyldig',
                                                    show: isValid
                                                        ? false
                                                        : kontaktTilNyansatDateError.show,
                                                })
                                            }}
                                            setSelected={(e) => {
                                                if (e) {
                                                    setShowSubmitButton(true)
                                                    setSelectedKontaktTilNyansatDate(
                                                        moment(e).format(
                                                            'YYYY-MM-DD'
                                                        )
                                                    )
                                                } else {
                                                    setShowSubmitButton(false)
                                                    setSelectedKontaktTilNyansatDate(
                                                        undefined
                                                    )
                                                }
                                            }}
                                            hasError={
                                                kontaktTilNyansatDateError.hasError &&
                                                kontaktTilNyansatDateError.show
                                            }
                                        />
                                        {kontaktTilNyansatDateError.show && (
                                            <FormMessage
                                                text={
                                                    kontaktTilNyansatDateError.message
                                                }
                                                error={
                                                    kontaktTilNyansatDateError.message
                                                }
                                            />
                                        )}
                                    </div>
                                )}
                        </>
                    )}
                {caseReportingStatusList &&
                    caseReportingStatusList.organisaeringStatusList && (
                        <div>
                            <RestrictElementWithTooltip
                                restrictedAction={
                                    !caseDetails?.allowedUpdateActionList?.includes(
                                        'Sagsbehandle'
                                    )
                                }
                            >
                                <Select
                                    options={caseReportingStatusList?.organisaeringStatusList?.map(
                                        (status) => ({
                                            label: status.statusName,
                                            value: status.statusId.toString(),
                                        })
                                    )}
                                    placeholder={`${L(
                                        'LØN_SAGS_AFRAPPORTERING_ORGANISERING_PLACEHOLDER'
                                    )} *`}
                                    name="medlemsskabStatusId"
                                    defaultValue={
                                        caseDetails.caseProcessing
                                            .salaryPlacementStatus.statusId
                                    }
                                    value={selectedMedlemsskabStatusId?.statusId.toString()}
                                    hasError={
                                        !!selectedMedlemsskabStatusId?.statusMissing &&
                                        caseDetails?.caseStatus?.statusType !==
                                            'AfventerAccept'
                                    }
                                    onChange={handleChangeMedlemsskabStatusId}
                                    disabled={
                                        !caseDetails?.allowedUpdateActionList?.includes(
                                            'Sagsbehandle'
                                        ) ||
                                        !!caseDetails?.caseProcessing
                                            ?.organizationStatusCannotBeUpdated ||
                                        authProfile.isImitator
                                    }
                                />
                            </RestrictElementWithTooltip>
                        </div>
                    )}
                {caseReportingStatusList &&
                    caseReportingStatusList.loenindplaceringStatusList && (
                        <div>
                            <RestrictElementWithTooltip
                                restrictedAction={
                                    !caseDetails?.allowedUpdateActionList?.includes(
                                        'Sagsbehandle'
                                    )
                                }
                            >
                                <Select
                                    options={caseReportingStatusList?.loenindplaceringStatusList?.map(
                                        (status) => ({
                                            label: status.statusName,
                                            value: status.statusId.toString(),
                                        })
                                    )}
                                    placeholder={`${L(
                                        'LØN_SAGS_AFRAPPORTERING_LØNINDPLACERING_PLACEHOLDER'
                                    )} *`}
                                    name="loenindplaceringStatusId"
                                    defaultValue={
                                        caseDetails.caseProcessing
                                            .salaryPlacementStatus.statusId
                                    }
                                    value={selectedLoenindplaceringStatusId?.statusId.toString()}
                                    hasError={
                                        !!selectedLoenindplaceringStatusId?.statusMissing &&
                                        caseDetails?.caseStatus?.statusType !==
                                            'AfventerAccept'
                                    }
                                    onChange={
                                        handleChangeLoenindplaceringStatusId
                                    }
                                    disabled={
                                        !caseDetails?.allowedUpdateActionList?.includes(
                                            'Sagsbehandle'
                                        ) || authProfile.isImitator
                                    }
                                />
                            </RestrictElementWithTooltip>
                        </div>
                    )}

                {showSubmitButton && (
                    <div
                        style={{
                            display: 'flex',
                            gap: '.7rem',
                            justifyContent: 'space-evenly',
                        }}
                    >
                        <Button
                            type="secondary"
                            style={{ flexGrow: 1 }}
                            onClick={resetForm}
                        >
                            {L('LØN_SAGS_AFRAPPORTERING_FORTRYD_KNAP')}
                        </Button>
                        <Button
                            type="primary"
                            style={{ flexGrow: 1 }}
                            onClick={handleSubmit}
                            loading={isLoading}
                        >
                            {L('LØN_SAGS_AFRAPPORTERING_GEM_KNAP')}
                        </Button>
                    </div>
                )}
            </form>
        )
    }

    return (
        <>
            <CardColumn
                titleLabel={L('LØN_SAGS_AFRAPPORTERING_CONTAINER_TITEL')}
                titleIcon={<IkonContainerAfrapportering fill={colors.green} />}
                rows={
                    caseDetails?.isClosed
                        ? renderRows()
                        : [
                              {
                                  label: '',
                                  value: '',
                                  useContent: true,
                                  content: <>{renderForm()}</>,
                              },
                          ]
                }
            />
            <ErrorDialogComponent />
        </>
    )
}

export { CaseReportingRow }
